import type {
  BrainCategory,
  BrainKind,
  BrainPriority,
  BrainText,
} from "./types";

export interface KindMeta {
  category: BrainCategory;
  title: BrainText;
  priority: BrainPriority;
  reason: BrainText;
  /** Lower sorts earlier inside a priority + scope bucket. */
  weight: number;
}

const m = (
  category: BrainCategory,
  kind: BrainKind,
  title: string,
  priority: BrainPriority,
  reason: string,
  weight: number
): KindMeta => ({
  category,
  title: { key: `brain.kind.${kind}.title`, fallback: title },
  priority,
  reason: { key: `brain.kind.${kind}.reason`, fallback: reason },
  weight,
});

export const KIND_META: Record<BrainKind, KindMeta> = {
  payout_ready: m(
    "payments",
    "payout_ready",
    "Pay out owner",
    "high",
    "Money owed to an owner",
    10
  ),
  reconcile: m(
    "payments",
    "reconcile",
    "Assign incoming payment",
    "high",
    "Unapplied money in the account",
    15
  ),
  late_rent: m(
    "payments",
    "late_rent",
    "Late rent",
    "low",
    "Daily visibility, not a single action",
    60
  ),
  room_base_drift: m(
    "tenants",
    "room_base_drift",
    "Rent or deposit differs from the room",
    "high",
    "Tenant billed differently from their room",
    16
  ),
  application_review: m(
    "tenants",
    "application_review",
    "Review application and decide",
    "high",
    "Applicant waiting on a decision",
    18
  ),
  stage_key_pickup_pending: m(
    "tenants",
    "stage_key_pickup_pending",
    "Set the key handover",
    "urgent",
    "Move-in is days away and no handover is set",
    20
  ),
  stage_handover_pending: m(
    "tenants",
    "stage_handover_pending",
    "Move-in protocol outstanding",
    "high",
    "Rent running on a tenant who isn't Active",
    21
  ),
  stage_deposit_pending: m(
    "tenants",
    "stage_deposit_pending",
    "Remind tenant to pay the deposit",
    "high",
    "No keys until the deposit lands",
    22
  ),
  stage_signature_pending: m(
    "tenants",
    "stage_signature_pending",
    "Get the contract signed",
    "high",
    "Room blocked until the contract is signed",
    23
  ),
  stage_status_ahead: m(
    "tenants",
    "stage_status_ahead",
    "Status ahead of evidence",
    "high",
    "Records disagree with the evidence",
    24
  ),
  stage_moveout_protocol_pending: m(
    "tenants",
    "stage_moveout_protocol_pending",
    "Move-out protocol window closing",
    "urgent",
    "The protocol window closes in days",
    25
  ),
  stage_key_return_pending: m(
    "tenants",
    "stage_key_return_pending",
    "Record the key return",
    "high",
    "Blocks the deposit refund",
    26
  ),
  stage_deposit_refund_pending: m(
    "tenants",
    "stage_deposit_refund_pending",
    "Settle the deposit refund",
    "high",
    "Tenant's money held after move-out",
    27
  ),
  stage_moveout_scheduled: m(
    "tenants",
    "stage_moveout_scheduled",
    "Confirm the move-out date",
    "normal",
    "Room can't be relisted without a confirmed date",
    28
  ),
  tenant_details_missing: m(
    "tenants",
    "tenant_details_missing",
    "Fill in missing tenant details",
    "high",
    "Missing details block the next stage",
    29
  ),
  application_incomplete: m(
    "tenants",
    "application_incomplete",
    "Application incomplete",
    "normal",
    "Waiting on the applicant's documents",
    30
  ),
  move_out_requested: m(
    "tenants",
    "move_out_requested",
    "Move-out requested",
    "high",
    "Tenant asked for a move-out date, awaiting a decision",
    31
  ),
  onboarding_review: m(
    "owners",
    "onboarding_review",
    "Review owner onboarding",
    "normal",
    "Owner waiting on a first answer",
    31
  ),
  property_submission: m(
    "owners",
    "property_submission",
    "Review submitted property",
    "normal",
    "Can't be listed until approved",
    32
  ),
  owner_invite_unsigned: m(
    "owners",
    "owner_invite_unsigned",
    "Management contract awaiting signature",
    "high",
    "Unsigned contract blocks the owner's onboarding",
    34
  ),
  owner_bank_missing: m(
    "properties",
    "owner_bank_missing",
    "Add owner bank details",
    "high",
    "No payout possible without bank details",
    35
  ),
  representor_missing: m(
    "properties",
    "representor_missing",
    "Set a representor",
    "high",
    "Contracts would print no representor",
    36
  ),
  property_setup_missing: m(
    "properties",
    "property_setup_missing",
    "Complete property setup",
    "normal",
    "Missing details on units and rooms",
    38
  ),
  lease_end_approaching: m(
    "owners",
    "lease_end_approaching",
    "Owner contract ends soon",
    "normal",
    "Decide whether to prolong",
    37
  ),
  ticket_unassigned: m(
    "support",
    "ticket_unassigned",
    "Assign new ticket",
    "high",
    "New ticket nobody owns yet",
    40
  ),
  ticket_unread: m(
    "support",
    "ticket_unread",
    "Read new ticket messages",
    "normal",
    "Waiting on a first read",
    42
  ),
  ticket_stale: m(
    "support",
    "ticket_stale",
    "Reply to stale ticket",
    "high",
    "No admin reply for 14 days",
    44
  ),
  empty_rooms: m(
    "tenants",
    "empty_rooms",
    "Empty or soon-empty rooms",
    "low",
    "Daily visibility, not a single action",
    61
  ),
};

export const PRIORITY_RANK: Record<BrainPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};
